import { Callout, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { ArgoCDUpdateAppFromTemplateProperties } from "@octopusdeploy/legacy-action-properties";
import { GitCommitMethods } from "@octopusdeploy/legacy-action-properties";
import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { ActionExecutionLocation, GetNamedPackageReferences, GetPrimaryPackageReference, InitialisePrimaryGitDependency, InitialisePrimaryPackageReference, RemovePrimaryGitDependency, RemovePrimaryPackageReference, SetPrimaryPackageReference, } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import * as _ from "lodash";
import type { PropsWithChildren } from "react";
import React from "react";
import { useOptionalProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { useGitCredentialsFromContext, useRefreshGitCredentialsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import { TargetRoles } from "~/areas/projects/components/Process/types";
import { buildRelatedTriggerDependencies } from "~/areas/projects/components/Triggers/ProcessCallouts/triggerPackageRelations";
import { useOptionalProjectContext } from "~/areas/projects/context/index";
import { ActionInputs } from "~/components/ActionInputs/ActionInputs";
import type { ActionSummaryProps } from "~/components/Actions/actionSummaryProps";
import type { ArgoCdInstancesAndApplications } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { useArgoCDContext } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { DeploymentPreviewExpander } from "~/components/Actions/argocd/DeploymentPreview";
import type { ActionWithDirtyState, ActionWithFeeds, ActionWithGitRepositorySource } from "~/components/Actions/commonActionHelpers";
import { validatePaths } from "~/components/Actions/kubernetes/kubernetesRawYamlAction";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { ScriptPackageProperties } from "~/components/Actions/script/ScriptPackageReferenceDialog";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import FeatureToggleVisibility from "~/components/FeatureToggle/New/FeatureToggleVisibility";
import { GitRepositorySourceSelector, isProjectRepositoryGitSourceSupported } from "~/components/GitRepositorySource/GitRepositorySourceSelector";
import PackageDownloadOptions from "~/components/PackageDownloadOptions/PackageDownloadOptions";
import { CardFill, type SummaryNode } from "~/components/form/Sections/ExpandableFormSection";
import ExpanderSectionHeading from "~/components/form/Sections/FormSectionHeading";
import { VariableLookupText } from "~/components/form/VariableLookupText";
import { ExpandableFormSection, Note, StringCheckbox, Summary } from "~/components/form/index";
import CommonSummaryHelper from "~/utils/CommonSummaryHelper/index";
import { codeEditorVariablesList } from "~/utils/ScriptIntellisense/scriptIntellisense";
class ArgoCDUpdateAppFromTemplateActionSummary extends BaseComponent<ActionSummaryProps, never> {
    constructor(props: ActionSummaryProps) {
        super(props);
    }
    render() {
        return <div>Update ArgoCD applications based on stored templates and Octopus variables</div>;
    }
    static displayName = "ArgoCDUpdateAppFromTemplateActionSummary";
}
type ArgoCDUpdateAppFromTemplateActionFields = ActionEditProps<ArgoCDUpdateAppFromTemplateProperties, ScriptPackageProperties>;
interface ArgoCDUpdateAppFromTemplateActionEditState {
    serverNames: string[];
}
export interface ArgoCDUpdateAppFromTemplateProps extends ArgoCDUpdateAppFromTemplateActionFields, ActionWithFeeds, ActionWithGitRepositorySource, ActionWithDirtyState {
    argoContext: InlineStatusQuery<ArgoCdInstancesAndApplications, null>;
}
export class ArgoCDUpdateAppFromTemplateActionEdit extends BaseComponent<ArgoCDUpdateAppFromTemplateProps, ArgoCDUpdateAppFromTemplateActionEditState> {
    constructor(props: ArgoCDUpdateAppFromTemplateProps) {
        super(props);
        this.state = {
            serverNames: [],
        };
    }
    private supportsProjectRepository() {
        return isProjectRepositoryGitSourceSupported(this.props.project?.PersistenceSettings, this.props.processType);
    }
    private onChangeTemplateSource(value: string) {
        this.props.setProperties({
            ["Octopus.Action.Script.ScriptSource"]: value,
        });
        if (value === "GitRepository") {
            if (this.supportsProjectRepository()) {
                this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "Project" });
                this.props.setGitDependencies?.(RemovePrimaryGitDependency(this.props.gitDependencies), false);
            }
            else {
                this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "External" });
                this.props.setGitDependencies?.(InitialisePrimaryGitDependency(this.props.gitDependencies), false);
            }
        }
        else {
            this.props.setGitDependencies?.(RemovePrimaryGitDependency(this.props.gitDependencies), false);
            this.props.setProperties({ ["Octopus.Action.GitRepository.FilePathFilters"]: undefined, ["Octopus.Action.GitRepository.Source"]: undefined });
        }
        if (value === "Package") {
            this.props.setPackages(InitialisePrimaryPackageReference(this.props.packages, this.props.feeds, this.props.actionInputsDependencies.type === "process-template"));
        }
        else {
            this.props.setPackages(RemovePrimaryPackageReference(this.props.packages));
        }
    }
    packageVariableNames = (): string[] => GetNamedPackageReferences(this.props.packages).flatMap((pkg) => [
        `Octopus.Action.Package[${pkg.Name}].PackageId`,
        `Octopus.Action.Package[${pkg.Name}].FeedId`,
        `Octopus.Action.Package[${pkg.Name}].PackageVersion`,
        `Octopus.Action.Package[${pkg.Name}].Path`,
        `Octopus.Action.Package[${pkg.Name}].Image`,
    ]);
    private summaryTemplates = () => {
        const source = this.props.properties["Octopus.Action.Script.ScriptSource"];
        if (source === "GitRepository") {
            return Summary.default("File(s) from a Git repository");
        }
        if (source === "Package") {
            return Summary.summary("File(s) inside a package");
        }
        return Summary.placeholder("Resource source not specified");
    };
    getPathsNotSetValidationMessage() {
        return "Enter the paths to your template files";
    }
    getPackagePathsAccessibleName() {
        return "Paths to the template files in your Package";
    }
    getPackagePathsLabel() {
        return "Kubernetes template file paths";
    }
    private setDefaultScriptSource() {
        this.props.setProperties({ ["Octopus.Action.Script.ScriptSource"]: "GitRepository" });
        if (this.supportsProjectRepository()) {
            this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "Project" });
        }
        else {
            this.props.setProperties({ ["Octopus.Action.GitRepository.Source"]: "External" });
            this.props.setGitDependencies?.(InitialisePrimaryGitDependency(this.props.gitDependencies), false);
        }
    }
    getCommitMethod(): string {
        return this.props.properties["Octopus.Action.ArgoCD.CommitMethod"] ?? GitCommitMethods.DirectCommit;
    }
    commitMethodSummary(): SummaryNode {
        if (this.getCommitMethod() === GitCommitMethods.DirectCommit) {
            return Summary.placeholder("Direct commit");
        }
        else {
            return Summary.summary(`Commit with pull request`);
        }
    }
    purgeOutputSummary(): SummaryNode {
        if (this.getPurgeOutputFolder()) {
            return Summary.summary("All existing app files in the Argo CD source folder will be deleted.");
        }
        else {
            return Summary.summary("Existing app files will remain in the Argo CD source folder.");
        }
    }
    getPurgeOutputFolder(): boolean {
        return this.props.properties["Octopus.Action.ArgoCD.PurgeOutputFolder"] === "True";
    }
    recursiveResourceDetectionSummary(): SummaryNode {
        if (this.recursiveResourceDetectionEnabled()) {
            return Summary.summary("All subfolders of the input path will be included.");
        }
        else {
            return Summary.summary("Subfolders will not be copied to the output repository.");
        }
    }
    async componentDidMount() {
        if (!this.props.properties["Octopus.Action.Script.ScriptSource"]) {
            this.setDefaultScriptSource();
        }
    }
    recursiveResourceDetectionEnabled(): boolean {
        return this.props.properties["Octopus.Action.ArgoCD.RecursiveResourceDetection"] === "True";
    }
    onInputPathChanged(value: string) {
        this.props.setProperties({ ["Octopus.Action.ArgoCD.InputPath"]: value });
        if (this.props.properties["Octopus.Action.Script.ScriptSource"] === "GitRepository") {
            if (value.endsWith("/")) {
                if (this.recursiveResourceDetectionEnabled()) {
                    value += "**/";
                }
                value += "*";
            }
            this.props.setProperties({ ["Octopus.Action.GitRepository.FilePathFilters"]: value });
        }
    }
    getFilePathSummary = (): SummaryNode => {
        const inputString = this.props.properties["Octopus.Action.ArgoCD.InputPath"];
        if (inputString === undefined) {
            return Summary.summary("Specify the file/folder to be used as input");
        }
        else {
            return Summary.summary(<>
                    <code>{inputString}</code> will provide input templates
                </>);
        }
    };
    getPathsNote = () => (<Note>
            Provide the path to a single file or directory. A directory path requires a trailing slash e.g. <code>directory/</code>
        </Note>);
    render() {
        const pkg = GetPrimaryPackageReference(this.props.packages);
        const pkgFeed = pkg ? this.props.feeds.find((f) => f.Id === pkg.FeedId) : undefined;
        const packageReferences = GetNamedPackageReferences(this.props.packages);
        const localNames = _.concat(this.props.localNames ? this.props.localNames : [], this.packageVariableNames());
        const autoComplete = codeEditorVariablesList(this.state.serverNames, localNames ?? []);
        return (<>
                <ExpanderSectionHeading title="Input" description="The source location for templates required for Argo App updates"/>
                <ExpandableFormSection errorKey="Octopus.Action.Script.ScriptSource|Octopus.Action.ArgoCD.InputPath" isExpandedByDefault={this.props.expandedByDefault} title="Template Source" fillCardWidth={CardFill.FillRight} summary={this.summaryTemplates()} help={"Select the source of the Kubernetes templates."}>
                    <RadioButtonGroup accessibleName="Template Source" value={this.props.properties["Octopus.Action.Script.ScriptSource"]} onChange={(val) => this.onChangeTemplateSource(val)} error={this.props.getFieldError("Octopus.Action.Script.ScriptSource")}>
                        <RadioButton accessibleName="Git Repository" value={"GitRepository"} label="Git Repository" isDefault={true}/>
                        <RadioButton accessibleName="Package" value={"Package"} label="Package"/>
                    </RadioButtonGroup>
                </ExpandableFormSection>
                {this.props.properties["Octopus.Action.Script.ScriptSource"] === "Package" && (<div>
                        <ExpandableFormSection errorKey="Octopus.Action.Package.FeedId|Octopus.Action.Package.PackageId" isExpandedByDefault={this.props.expandedByDefault} title="Package" summary={CommonSummaryHelper.packageSummary(pkg, this.props.feeds)} help={"Choose the package that contains the Kubernetes templates."}>
                            <ActionInputs actionUIDefinition={[{ type: "deferred-package-selector", key: "deferred-package-selector", hasModelDirty: true, hasRelatedTriggersDependencies: true }]} dependencies={this.props.actionInputsDependencies}/>
                            <PackageDownloadOptions packageAcquisitionLocation={pkg.AcquisitionLocation} onPackageAcquisitionLocationChanged={(acquisitionLocation) => this.props.setPackages(SetPrimaryPackageReference({ AcquisitionLocation: acquisitionLocation }, this.props.packages))} feed={pkgFeed} projectId={this.props.projectId} localNames={this.props.localNames}/>
                        </ExpandableFormSection>
                    </div>)}
                {this.props.properties["Octopus.Action.Script.ScriptSource"] === "GitRepository" && (<GitRepositorySourceSelector properties={this.props.properties} gitCredentials={this.props.gitCredentials} refreshGitCredentials={this.props.refreshGitCredentials} project={this.props.project} processType={this.props.processType} gitDependencies={this.props.gitDependencies} expandedByDefault={this.props.expandedByDefault} getFieldError={this.props.getFieldError} setProperties={this.props.setProperties} setGitDependencies={this.props.setGitDependencies} localNames={this.props.localNames} enableFilePathFilters={false} relatedTriggerDependencies={buildRelatedTriggerDependencies(this.props.inputDependencies)} isStepFromProcessTemplate={this.props.isStepFromProcessTemplate}/>)}
                <ExpandableFormSection errorKey="Path" title={"Input Path"} summary={this.getFilePathSummary()} help={"Provide the path to your input files or directory."} isExpandedByDefault={this.props.expandedByDefault}>
                    <VariableLookupText localNames={this.props.localNames} value={this.props.properties["Octopus.Action.ArgoCD.InputPath"]} onChange={(x) => this.onInputPathChanged(x)} label={this.getPackagePathsLabel()} error={this.props.getFieldError("Octopus.Action.ArgoCD.InputPath")} validate={(p) => validatePaths(p, this.getPathsNotSetValidationMessage())} accessibleName={this.getPackagePathsAccessibleName()} multiline={false}/>
                    {this.getPathsNote()}
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.RecursiveResourceDetection" title="Recursive Resource Detection" summary={this.recursiveResourceDetectionSummary()} help="Configure which files and resources Octopus should include from the input path." isExpandedByDefault={this.props.expandedByDefault}>
                    <StringCheckbox value={this.props.properties["Octopus.Action.ArgoCD.RecursiveResourceDetection"]} onChange={(val) => this.props.setProperties({ ["Octopus.Action.ArgoCD.RecursiveResourceDetection"]: val })} label={"Enable recursive detection"}/>
                </ExpandableFormSection>
                <ExpanderSectionHeading title="Output" description="The files from the input will be populated with Octopus variables, and written to the git repo of your mapped Argo CD applications."/>
                <DeploymentPreviewExpander argoContext={this.props.argoContext}/>
                <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.CommitMessage" title="Commit Message" summary={!!this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"] ? Summary.summary(this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"]) : Summary.summary("No summary provided")} help={"Provide a message for the commit to Git."} isExpandedByDefault={this.props.expandedByDefault}>
                    <VariableLookupText key="Octopus.Action.UpdateAppFromTemplates.Destination.CommitMessageSummary" localNames={this.props.localNames} value={this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"] ?? ""} onChange={(summary) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageSummary"]: summary })} label={"Summary"} error={this.props.getFieldError("Octopus.Action.UpdateAppFromTemplates.Destination.CommitMessageSummary")} multiline={false}/>
                    <VariableLookupText key="Octopus.Action.UpdateAppFromTemplates.Destination.CommitMessageDescription" localNames={this.props.localNames} value={this.props.properties["Octopus.Action.ArgoCD.CommitMessageDescription"] ?? ""} onChange={(description) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageDescription"]: description })} label={"Optional description"} error={this.props.getFieldError("Octopus.Action.UpdateAppFromTemplates.Destination.CommitMessageDescription")} multiline={true} helperText={"Octopus will use only the summary if no description is supplied."}/>
                </ExpandableFormSection>
                <FeatureToggleVisibility toggle={"argocd-create-pull-request"}>
                    <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.CommitMethod" title="Git Commit Method" summary={this.commitMethodSummary()} help={"Select how you want to commit your configuration to Git."} isExpandedByDefault={this.props.expandedByDefault}>
                        <RadioButtonGroup value={this.getCommitMethod()} onChange={(val) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMethod"]: val })} error={this.props.getFieldError("Octopus.Action.ArgoCD.CommitMethod")}>
                            <RadioButton value={GitCommitMethods.DirectCommit} label="Direct commit"/>
                            <RadioButton value={GitCommitMethods.PullRequest} label="Commit with pull request"/>
                        </RadioButtonGroup>
                    </ExpandableFormSection>
                </FeatureToggleVisibility>
                <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.PurgeOutputFolder" title="Purge Output Folder" summary={this.purgeOutputSummary()} help="Delete all manifest files in the output folder that weren’t created during the current deployment." isExpandedByDefault={this.props.expandedByDefault}>
                    <StringCheckbox value={this.props.properties["Octopus.Action.ArgoCD.PurgeOutputFolder"]} onChange={(val) => this.props.setProperties({ ["Octopus.Action.ArgoCD.PurgeOutputFolder"]: val })} label={"Enable output folder purging"}/>
                    {this.getPurgeOutputFolder() && (<Callout type={"warning"} hideTitle={true}>
                            Purging permanently deletes all existing files the output folder.
                        </Callout>)}
                </ExpandableFormSection>
            </>);
    }
    static displayName = "ArgoCDUpdateAppFromTemplateActionEdit";
}
export function ArgoCDUpdateAppFromTemplateAction(props: PropsWithChildren<ArgoCDUpdateAppFromTemplateActionFields>) {
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const gitCredentials = useGitCredentialsFromContext();
    const refreshGitCredentials = useRefreshGitCredentialsFromContext();
    const projectContext = useOptionalProjectContext();
    const processContext = useOptionalProcessContext();
    return (<ArgoCDUpdateAppFromTemplateActionEdit {...props} feeds={feeds} refreshFeeds={refreshFeeds} project={projectContext?.state.model} processType={processContext?.selectors.getProcessType()} gitCredentials={gitCredentials} refreshGitCredentials={refreshGitCredentials} modelDirty={!isEqual(processContext?.state.model, processContext?.state.cleanModel)} argoContext={useArgoCDContext()}/>);
}
pluginRegistry.registerAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    canRunOnWorker: true,
    canRunInContainer: true,
    disableInlineExecutionContainers: true,
    actionType: "Octopus.ArgoCD.TemplateUpdate",
    summary: (properties, targetRolesAsCSV) => <ArgoCDUpdateAppFromTemplateActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV}/>,
    editSections: {
        default: (props: ArgoCDUpdateAppFromTemplateActionFields) => <ArgoCDUpdateAppFromTemplateAction {...props}/>,
    },
    getInitialProperties: () => ({ "Octopus.Action.ArgoCD.CommitMessageSummary": defaultCommitMessageSummary }),
    canHaveChildren: (step) => false,
    canBeChild: true,
    targetRoleOption: (action) => TargetRoles.None,
    docsLink: "updateArgoCDAppFromTemplate",
});
const defaultCommitMessageSummary = "Octopus Deploy updated image versions";
